import {
  cloneElement,
  FC,
  isValidElement,
  MouseEventHandler,
  ReactElement,
  ReactNode,
  Ref,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { twJoin, twMerge } from 'tailwind-merge';

import { HoverCard, HoverCardContent, HoverCardTrigger } from '../hover-card/index';

export enum Cursor {
  DEFAULT = 'default',
  POINTER = 'pointer',
  QUESTION = 'question',
}

const cursorClassName: Record<Cursor, string> = {
  [Cursor.DEFAULT]: 'cursor-default',
  [Cursor.POINTER]: 'cursor-pointer',
  [Cursor.QUESTION]: 'cursor-help',
};

const isTag =
  <T extends readonly string[]>(...tags: T) =>
  (node: ReactNode): node is ReactElement<unknown, T[number]> =>
    isValidElement(node) && typeof node.type === 'string' && tags.includes(node.type);

const isStringTag = isTag('span', 'p', 'link', 'h2', 'h3', 'h4', 'h5', 'h6');

type TooltipMeasuredChildProps = {
  className?: string;
  onMouseEnter?: MouseEventHandler;
  ref?: Ref<HTMLElement>;
};

export type TooltipProps = {
  children: ReactNode;
  childrenWrapperClassName?: string;
  className?: string;
  content: ReactNode;
  cursor?: Cursor;
  disabled?: boolean;
  placement?: 'bottom' | 'left' | 'right' | 'top';
};

export const useEllipsis = <T extends HTMLElement>() => {
  const ref = useRef<null | T>(null);
  const [ellipsized, setEllipsized] = useState(false);

  const recompute = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const next = el.scrollWidth > el.clientWidth;
    setEllipsized(next);
  }, []);

  return { ellipsized, recompute, ref };
};

export const Tooltip: FC<TooltipProps> = ({
  children,
  childrenWrapperClassName,
  className,
  content,
  disabled,
  cursor = Cursor.DEFAULT,
  placement = 'top',
}) => {
  const { ellipsized, recompute, ref } = useEllipsis<HTMLElement>();

  const handleMouseEnter = () => {
    recompute();
  };

  let measuredChild: ReactElement;

  const shouldShow = useMemo(() => {
    if (disabled || !content) return false;
    if (isValidElement(children) && !isStringTag(children)) return true;
    return ellipsized;
  }, [disabled, content, children, ellipsized]);

  if (isValidElement(children)) {
    const child = children as ReactElement<TooltipMeasuredChildProps>;
    measuredChild = cloneElement(child, {
      className: twJoin(
        'truncate',
        childrenWrapperClassName,
        cursorClassName[cursor],
        'align-top block',
        child.props?.className,
      ),
      onMouseEnter: handleMouseEnter,
      ref: ref,
    });
  } else {
    measuredChild = (
      <span
        className={twMerge(
          'block max-w-max truncate align-top',
          cursorClassName[cursor],
          childrenWrapperClassName,
        )}
        onMouseEnter={handleMouseEnter}
        ref={ref}
      >
        {children}
      </span>
    );
  }

  if (!shouldShow) return measuredChild;

  return (
    <HoverCard>
      <HoverCardTrigger asChild>{measuredChild}</HoverCardTrigger>

      <HoverCardContent
        className={twMerge('max-w-fit rounded-lg bg-[#101828] px-3 py-2', className)}
        side={placement}
      >
        <span className="block w-full p-0 text-center text-label-md font-[550] text-white">
          {content}
        </span>
      </HoverCardContent>
    </HoverCard>
  );
};
